import {
  ArrayPath,
  getArrayPath,
  getStringPath,
  pathStartsWith,
  setByPath,
} from '@the-platform-company/path-utilities'
import { MutableRefObject } from 'react'
import { LOCAL_STORAGE_PREFIX } from './constants'
import { Listener, State, Store } from './types'
import { getStatePath } from './utils'

export const createStore = (initialState?: State): Store => {
  let dispatchTimeout: NodeJS.Timer | undefined
  let currentState: State = initialState ?? {}
  const currentListeners = new Set<Listener>([])

  const getState = () => currentState

  const subscribe = (listener: Listener) => {
    currentListeners.add(listener)
    return () => {
      currentListeners.delete(listener)
    }
  }

  const dispatch = () => {
    if (dispatchTimeout) {
      clearTimeout(dispatchTimeout)
    }

    dispatchTimeout = setTimeout(() => {
      for (const listener of Array.from(currentListeners)) {
        listener(getState())
      }
    })
  }

  const setState = (path: string | ArrayPath, value: unknown) => {
    const arrayPath = Array.isArray(path) ? path : getArrayPath(path)

    if (pathStartsWith(arrayPath, LOCAL_STORAGE_PREFIX)) {
      const localStoragePath = getStringPath(arrayPath.slice(1))

      if (value == null) {
        window.localStorage.removeItem(localStoragePath)
      } else {
        window.localStorage.setItem(localStoragePath, JSON.stringify(value))
      }
    } else {
      currentState = setByPath(currentState, path, value)
    }

    dispatch()
  }

  return {
    getState,
    setState,
    subscribe,
    dispatch,
  }
}

export const createStoreWithAdditionalState = (
  rootStore: Store,
  upstreamStore: Store,
  additionalStateRef: MutableRefObject<State | undefined>,
  namespace?: string
): Store => {
  let prevState: State | null = null
  let prevAdditional: State | null = null
  let prevStateWithAdditional: State = {}
  let prevNamespace: string | null = null

  let dispatchTimeout: NodeJS.Timer | undefined
  const currentListeners = new Set<Listener>([])

  const setState = (path: string | ArrayPath, value: unknown) =>
    rootStore.setState(namespace ? getStatePath(path, namespace) : path, value)

  const getState = () => {
    const currentState = upstreamStore.getState()

    if (
      (currentState ?? null) === (prevState ?? null) &&
      (namespace ?? null) === (prevNamespace ?? null) &&
      (additionalStateRef.current ?? null) === (prevAdditional ?? null)
    ) {
      return prevStateWithAdditional
    }

    prevState = currentState ?? null
    prevNamespace = namespace ?? null
    prevAdditional = additionalStateRef.current ?? null
    prevStateWithAdditional = namespace
      ? {
          ...currentState,
          ...(currentState[namespace] as State),
          ...additionalStateRef.current,
        }
      : {
          ...currentState,
          ...additionalStateRef.current,
        }

    return prevStateWithAdditional
  }

  const subscribe = (listener: Listener) => {
    const unsubscribe = upstreamStore.subscribe(() => listener(getState()))
    currentListeners.add(listener)
    return () => {
      unsubscribe()
      currentListeners.delete(listener)
    }
  }

  const dispatch = () => {
    if (dispatchTimeout) {
      clearTimeout(dispatchTimeout)
    }

    dispatchTimeout = setTimeout(() => {
      for (const listener of Array.from(currentListeners)) {
        listener(getState())
      }
    })
  }

  return {
    setState,
    subscribe,
    dispatch,
    getState,
  }
}
